import React, { createContext, useState, useContext } from 'react';
import {
  createProject as createProjectService,
  updateProject as updateProjectService,
  matchCrew as matchCrewService,
  assignCrew as assignCrewService,
  matchEquipment as matchEquipmentService,
  assignEquipment as assignEquipmentService,
  createProjectBrief as createProjectBriefService,
  getProject as getProjectService,
} from '../services/projectService';

const ProjectContext = createContext(null);

export const ProjectProvider = ({ children }) => {
  const [currentProject, setCurrentProject] = useState(null);
  const [matchedCrew, setMatchedCrew] = useState([]);
  const [matchedEquipment, setMatchedEquipment] = useState([]);
  const [selectedCrewIds, setSelectedCrewIds] = useState([]);
  const [selectedEquipmentIds, setSelectedEquipmentIds] = useState([]);
  const [assignedCrewDetails, setAssignedCrewDetails] = useState([]);
  const [assignedEquipmentDetails, setAssignedEquipmentDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Helper function to parse JSON string fields in project object
   * @param {Object} project - Project object from backend
   * @returns {Object} Project with parsed array fields
   */
  // const parseProjectData = (projectData) => {
  //   if (!projectData) return null;

  //   // Handle nested booking object or direct project object
  //   const project = projectData.booking || projectData;

  //   // Parse JSON string fields to arrays
  //   const parseJsonField = (field) => {
  //     if (!field) return [];
  //     if (Array.isArray(field)) return field;
  //     try {
  //       const parsed = JSON.parse(field);
  //       return Array.isArray(parsed) ? parsed : [];
  //     } catch (e) {
  //       return [];
  //     }
  //   };

  //   return {
  //     ...project,
  //     streaming_platforms: parseJsonField(project.streaming_platforms),
  //     crew_roles: parseJsonField(project.crew_roles),
  //     skills_needed: parseJsonField(project.skills_needed),
  //     required_skills: parseJsonField(project.skills_needed), // Alias
  //     equipments_needed: parseJsonField(project.equipments_needed),
  //     equipment_categories_needed: parseJsonField(project.equipments_needed), // Alias
  //     project_id: project.stream_project_booking_id || project.project_id || project.id,
  //     id: project.stream_project_booking_id || project.id,
  //   };
  // };"
  //


  const parseProjectData = (projectData) => {
  if (!projectData) return null;

  // Sometime APIs return { project: {...} } or { booking: {...} } or just {...}
  // This ensures we get the actual object regardless of the wrapper
  const project = projectData.project || projectData.booking || projectData.data || projectData;

  const parseJsonField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try {
      const parsed = JSON.parse(field);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  return {
    ...project,
    streaming_platforms: parseJsonField(project.streaming_platforms),
    crew_roles: parseJsonField(project.crew_roles),
    skills_needed: parseJsonField(project.skills_needed || project.required_skills),
    required_skills: parseJsonField(project.skills_needed || project.required_skills),
    equipments_needed: parseJsonField(project.equipments_needed || project.equipment_categories_needed),
    project_id: project.stream_project_booking_id || project.id,
    id: project.stream_project_booking_id || project.id,
  };
};

  /**
   * Create a new project
   * @param {FormData} formData - Project creation data
   * @returns {Promise<Object>} Response with project_id
   */
  const createProject = async (formData) => {
    setLoading(true);
    setError(null);

    const response = await createProjectService(formData);

    if (response.success) {
      const parsedProject = parseProjectData(response.data);
      setCurrentProject(parsedProject);
    } else {
      setError(response.error);
      console.error('Failed to create project:', response.error);
    }

    setLoading(false);
    return response;
  };

const updateProject = async (projectId, formData) => {
  setLoading(true);
  setError(null);

  const response = await updateProjectService(projectId, formData);

  if (response.success) {
    // FIX: Look for data in response.data (standard) or response.data.data
    const projectContent = response.data?.data || response.data;
    const parsedProject = parseProjectData(projectContent);
    setCurrentProject(parsedProject);
    console.log("Context: Project updated and set to state", parsedProject);
  } else {
    setError(response.error);
  }

  setLoading(false);
  return response;
};

  /**
   * Match crew members for the current project
   * @returns {Promise<Object>} Response with matched crew list
   */
  const matchCrew = async () => {
    if (!currentProject) {
      console.error('No current project to match crew for');
      return { success: false, error: 'No current project' };
    }

    setLoading(true);
    setError(null);

    // Extract requirements from currentProject
    const matchParams = {
      crew_roles: currentProject.crew_roles || [],
      required_skills: currentProject.skills_needed || currentProject.required_skills || [],
      crew_size_needed: currentProject.crew_size_needed || 5,
      location: null, // TODO: Implement proper location-based matching (event_location is lat/lng JSON, crew location is city name)
    };

    const response = await matchCrewService(matchParams);

    if (response.success) {
      setMatchedCrew(response.data || []);
    } else {
      setError(response.error);
      console.error('Failed to match crew:', response.error);
    }

    setLoading(false);
    return response;
  };

  /**
   * Assign selected crew members to the project
   * @param {string} projectId - Project ID
   * @param {Array<string>} crewIds - Array of crew member IDs to assign
   * @returns {Promise<Object>} Response
   */
  const assignCrew = async (projectId, crewIds) => {
    setLoading(true);
    setError(null);

    const response = await assignCrewService({
      project_id: projectId,
      crew_ids: crewIds,
    });

    if (response.success) {
      // Save selected crew IDs to context
      setSelectedCrewIds(crewIds);

      // Get full details of assigned crew from matched crew
      const assignedDetails = matchedCrew.filter(crew =>
        crewIds.includes(String(crew.crew_member_id))
      );
      setAssignedCrewDetails(assignedDetails);
    } else {
      setError(response.error);
      console.error('Failed to assign crew:', response.error);
    }

    setLoading(false);
    return response;
  };

  /**
   * Helper function to aggregate equipment from multiple crew members
   * Removes duplicates and combines owned and pickup equipment
   * @param {Array} crewEquipmentData - [{crew_id, has: [...], needs_pick: [...]}]
   * @returns {Array} Flat list of unique equipment items
   */
  const aggregateCrewEquipment = (crewEquipmentData) => {
    const equipmentMap = new Map();

    crewEquipmentData.forEach(crewData => {
      // Add owned equipment (has)
      (crewData.has || []).forEach(equipment => {
        if (!equipmentMap.has(equipment.equipment_id)) {
          equipmentMap.set(equipment.equipment_id, {
            ...equipment,
            ownership_status: 'owned',
            crew_id: crewData.crew_id,
          });
        }
      });

      // Add equipment that needs pickup (needs_pick)
      (crewData.needs_pick || []).forEach(equipment => {
        if (!equipmentMap.has(equipment.equipment_id)) {
          equipmentMap.set(equipment.equipment_id, {
            ...equipment,
            ownership_status: 'pickup',
            crew_id: crewData.crew_id,
          });
        }
      });
    });

    // Convert map to array
    return Array.from(equipmentMap.values());
  };

  /**
   * Match equipment for the current project
   * @param {Object} data - { crew_id: [...], equipments_needed: [...] }
   * @returns {Promise<Object>} Response with matched equipment list
   */
  const matchEquipment = async (data) => {
    setLoading(true);
    setError(null);

    const response = await matchEquipmentService(data);

    if (response.success) {
      // API returns per-crew equipment directly in response.data: [{crew_id, has: [...], needs_pick: [...]}]
      // Aggregate into single flat list removing duplicates
      const crewEquipmentData = response.data || [];

      // Aggregate all equipment across all crew members
      const aggregatedEquipment = aggregateCrewEquipment(crewEquipmentData);

      setMatchedEquipment(aggregatedEquipment);
    } else {
      setError(response.error);
      console.error('Failed to match equipment:', response.error);
    }

    setLoading(false);
    return response;
  };

  /**
   * Assign selected equipment to the project
   * @param {string} projectId - Project ID
   * @param {Array<string>} equipmentIds - Array of equipment IDs to assign
   * @returns {Promise<Object>} Response
   */
  const assignEquipment = async (projectId, equipmentIds, equipmentDetails = []) => {
    setLoading(true);
    setError(null);

    const response = await assignEquipmentService({
      project_id: projectId,
      equipment_ids: equipmentIds,
    });

    if (response.success) {
      // Save selected equipment IDs to context
      setSelectedEquipmentIds(equipmentIds);

      // Save full equipment details for display on final screen
      setAssignedEquipmentDetails(equipmentDetails);
    } else {
      setError(response.error);
      console.error('Failed to assign equipment:', response.error);
    }

    setLoading(false);
    return response;
  };

  /**
   * Create final project brief
   * @param {FormData} formData - Project brief data
   * @returns {Promise<Object>} Response
   */
  const createBrief = async (formData) => {
    setLoading(true);
    setError(null);

    const response = await createProjectBriefService(formData);

    if (!response.success) {
      setError(response.error);
      console.error('Failed to create project brief:', response.error);
    }

    setLoading(false);
    return response;
  };

  /**
   * Get project details by ID
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Response with project details
   */
  const getProject = async (projectId) => {
    setLoading(true);
    setError(null);

    const response = await getProjectService(projectId);

    if (response.success) {
      const parsedProject = parseProjectData(response.data);
      setCurrentProject(parsedProject);
    } else {
      setError(response.error);
      console.error('Failed to get project:', response.error);
    }

    setLoading(false);
    return response;
  };

  /**
   * Reset project state
   */
  const resetProject = () => {
    setCurrentProject(null);
    setMatchedCrew([]);
    setMatchedEquipment([]);
    setSelectedCrewIds([]);
    setSelectedEquipmentIds([]);
    setAssignedCrewDetails([]);
    setAssignedEquipmentDetails([]);
    setError(null);
  };

  const value = {
    // State
    currentProject,
    matchedCrew,
    matchedEquipment,
    selectedCrewIds,
    updateProject,
    selectedEquipmentIds,
    assignedCrewDetails,
    assignedEquipmentDetails,
    loading,
    error,

    // Methods
    createProject,
    matchCrew,
    assignCrew,
    matchEquipment,
    assignEquipment,
    createBrief,
    getProject,
    resetProject,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
};

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within ProjectProvider');
  }
  return context;
};
